import { useLayoutEffect, useRef, useState } from 'react';

import { useTranslator } from '@picsart/rc/services/localization';
import { Segments, Segment } from '@picsart/design-system/components/Segments';
import { IconDiscoverFilled } from '@picsart/design-system/foundation/icons/IconDiscoverFilled';

import { useAppStore, type IAppStore } from '@/store';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ClickItem, ScreenType } from '@/types/analytics';
import { HOME_TOOLS } from '@/consts/homeTools';
import { HOME_FEED_SLUG } from '@/store/homeTabs/types';

import useStyles from './styles';

const selector = (store: IAppStore) => ({
  activeHomeTab: store.activeHomeTab,
  setActiveHomeTab: store.setActiveHomeTab,
});

/**
 * Sticky top-of-home tab row built from the DS `Segments` control — the default
 * feed ("All") followed by one `Segment` per registered tool (`HOME_TOOLS`).
 * The DS per-segment fill is removed; instead a single pill slides behind the
 * active segment. Its box is measured from the active segment (against the
 * wrapper, not the DS ref) and applied inline, so switching animates the fill.
 */
const HomeToolTabs = () => {
  const classes = useStyles();
  const translate = useTranslator();
  const { activeHomeTab, setActiveHomeTab } = useAppStore(selector);

  // "All" is the feed itself, not a tool, so its icon lives here rather than in
  // the `HOME_TOOLS` registry.
  const tabs = [
    { slug: HOME_FEED_SLUG, title: translate('All'), Icon: IconDiscoverFilled },
    ...HOME_TOOLS.map(({ slug, title, Icon }) => ({
      slug,
      title: translate(title),
      Icon,
    })),
  ];

  const wrapRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ left: 0, top: 0, width: 0, height: 0 });

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const active = wrap.querySelector<HTMLElement>(
      `[data-testid="home-tab-${activeHomeTab}"]`
    );
    if (!active) return undefined;

    // The row scrolls now, so an activated tab can sit outside the viewport —
    // `?tool=trending` lands on the last one. `nearest` is a no-op when it is
    // already visible, so this only moves the row when it has to.
    active.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    const update = () => {
      const activeRect = active.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      setPill({
        left: activeRect.left - wrapRect.left + wrap.scrollLeft,
        top: activeRect.top - wrapRect.top + wrap.scrollTop,
        width: activeRect.width,
        height: activeRect.height,
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(active);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [activeHomeTab, tabs.length]);

  const handleTabClick = (slug: string) => {
    setActiveHomeTab(slug);
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CLICK,
      screen_type: ScreenType.CHOOSER,
      item: ClickItem.Tool,
      settings: { tool: slug },
    });
  };

  return (
    <div className={classes.container} data-testid="home-tool-tabs">
      <div ref={wrapRef} className={classes.wrap}>
        <span
          aria-hidden
          className={classes.pill}
          style={{
            transform: `translateX(${pill.left}px)`,
            top: pill.top,
            width: pill.width,
            height: pill.height,
          }}
        />
        <Segments
          className={classes.segments}
          dataTestId="home-tool-segments"
          aria-label={translate('Home sections')}
        >
          {tabs.map(({ slug, title, Icon }) => (
            <Segment
              key={slug}
              isActive={activeHomeTab === slug}
              className={classes.segment}
              startIcon={Icon}
              onClick={() => handleTabClick(slug)}
              dataTestId={`home-tab-${slug}`}
            >
              {title}
            </Segment>
          ))}
        </Segments>
      </div>
    </div>
  );
};

export default HomeToolTabs;
